import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import EditableFieldRow from '../../custom/EditableFieldRow';
import { useAccordionState } from '../../custom/ExpandableGroup';
import { Icon } from '@trlogic/ui-pack';
import { AccordionIndicator, AccordionOpenCloseIcon } from '../../custom/AccordionIndicators';
import SarAccordionSectionHeader from './SarAccordionSectionHeader';
import SarValidationCategoryMapper from '../../../util/SarValidationCategoryMapper';
import { SarValidationResult } from '../SarInterfaces';
import SarParser from '../SarParser';

const SupheKategorileriAccordion = ({ xmlContent, mutateXmlContent, validate }: Props) => {
  const invalidFieldIds = useMemo<Set<string>>(() => {
    if (!validate || !validate.errors || validate.errors.length === 0) {
      return new Set<string>();
    }
    return SarValidationCategoryMapper.getInvalidFieldIds(validate.errors);
  }, [validate]);
  const supheKategorileri = useMemo(() => SarParser.parseSupheKategorileri(xmlContent), [xmlContent]);
  const accordion = useAccordionState();

  const errorsArray = validate?.errors ?? [];
  const errorsKey = errorsArray.map((e) => `${e.lineStart}-${e.lineEnd}-${e.message}`).join('|');
  const kategoriErrors = useMemo<Map<number, boolean>>(() => {
    const errors = new Map<number, boolean>();
    const currentInvalidFieldIds = errorsArray.length > 0 ? SarValidationCategoryMapper.getInvalidFieldIds(errorsArray) : new Set<string>();

    supheKategorileri.forEach((_, index) => {
      const kategoriPrefix = `suphekategorisi-${index}`;
      const hasError = Array.from(currentInvalidFieldIds).some((fieldId) => {
        return fieldId.startsWith(kategoriPrefix);
      });
      errors.set(index, hasError);
    });
    return errors;
  }, [supheKategorileri.length, errorsKey]);

  const handleChangeCategory = useCallback(
    (index: number, value: string) => {
      mutateXmlContent((document) => {
        let parent = document.getElementsByTagName('suphekategorileri').item(0);
        if (!parent) {
          parent = document.createElement('suphekategorileri');
          document.documentElement.appendChild(parent);
        }
        const categoryNode = parent.getElementsByTagName('suphekategorisi').item(index);
        if (!categoryNode) return;
        categoryNode.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleAddCategory = () => {
    mutateXmlContent((document) => {
      let parent = document.getElementsByTagName('suphekategorileri').item(0);
      if (!parent) {
        parent = document.createElement('suphekategorileri');
        document.documentElement.appendChild(parent);
      }
      const categoryNode = document.createElement('suphekategorisi');
      categoryNode.textContent = '';
      parent.appendChild(categoryNode);
    });
  };

  const outerLine1ClassName = 'flex w-full rounded-t-xl items-center justify-between px-5 2xl:px-6 h-[56px] 2xl:h-[58px] text-left border-b';

  return (
    <div className={'h-full w-full pr-6 pt-3 2xl:pt-4 flex flex-col gap-y-3'}>
      <div className={'flex items-center justify-between'}>
        <div className={'text-[#1D2939] text-base font-semibold'}>XML Document</div>
        <button
          onClick={handleAddCategory}
          type='button'
          className='w-5 h-5 hover:bg-f-gray-2 rounded-sm flex items-center justify-center transition-all duration-200'
        >
          <Icon icon='add' className='text-[#1D2939]' />
        </button>
      </div>
      {supheKategorileri.length === 0 ? (
        <div className={'rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-6 py-10 text-center text-sm text-[#667085]'}>
          Henüz şüphe kategorisi bulunmuyor.
        </div>
      ) : (
        <div className={'flex flex-col border rounded-[10px] h-full overflow-y-auto overflow-hidden'}>
          {supheKategorileri.map((supheKategorisi, index) => {
            const key = `suphe-kategori-${index}`;
            const expanded = accordion.isExpanded(key);
            const hasKategoriError = kategoriErrors.get(index) ?? false;
            return (
              <div key={key} className={'bg-white rounded-t-xl rounded-b-xl'}>
                <div
                  role={'button'}
                  tabIndex={0}
                  className={`${outerLine1ClassName} ${index === supheKategorileri.length - 1 ? '' : 'border-b'}`}
                  onClick={() => accordion.toggle(key)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      accordion.toggle(key);
                    }
                  }}
                >
                  <div className='flex items-center gap-x-3'>
                    <div>
                      <AccordionOpenCloseIcon expanded={expanded} hasError={hasKategoriError} />
                    </div>
                    <SarAccordionSectionHeader label={`Şüphe Kategorisi ${index + 1}`} fieldPath={'supheKategorileri'} hasError={hasKategoriError} />
                  </div>
                  <AccordionIndicator expanded={expanded} />
                </div>
                {expanded && (
                  <div className={'flex flex-col divide-y divide-[#F2F4F7] border-b'}>
                    <EditableFieldRow
                      label={'Şüphe Kategorisi'}
                      value={supheKategorisi}
                      onChange={(value) => handleChangeCategory(index, value)}
                      hasError={invalidFieldIds.has(`suphekategorisi-${index}`)}
                      infoConfig={{ path: 'supheKategorileri.supheKategorisi', type: 'Kod', mode: 'bubble' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface Props {
  xmlContent?: string;
  mutateXmlContent: (mutator: (document: Document) => void) => void;
  validate?: SarValidationResult;
}

export default SupheKategorileriAccordion;
